import { reportAnalyticsEvent } from '../analytics/webAnalyticsReporter'
import { API_PREFIX, MAP_SET_PATH } from '../schema/mapSet'
import { BundleFileStore } from './bundleFileStore'
import { DeliveryStore } from './deliveryStore'
import { MapBundleSession } from './mapBundleSession'

let session: MapBundleSession | null = null

/** In-memory storage for pages where `localStorage` is unavailable. */
function memoryStorage(): Pick<Storage, 'getItem' | 'setItem'> {
  const items = new Map<string, string>()
  return { getItem: (k) => items.get(k) ?? null, setItem: (k, v) => void items.set(k, v) }
}

function safeLocalStorage(): Pick<Storage, 'getItem' | 'setItem'> {
  try {
    return window.localStorage ?? memoryStorage()
  } catch {
    return memoryStorage()
  }
}

/**
 * The page's map delivery session, wired to the browser: Cache Storage for
 * files (absent in insecure contexts and in tests → the bundled map only),
 * `localStorage` for the decision state, the production-gated analytics
 * reporter, and a page reload to fall back after a broken download.
 */
export function getMapBundleSession(): MapBundleSession {
  session ??= new MapBundleSession({
    fetch: (input, init) => (init === undefined ? fetch(input) : fetch(input, init)),
    store: new DeliveryStore(safeLocalStorage()),
    files: typeof caches === 'undefined' ? null : new BundleFileStore(caches),
    report: reportAnalyticsEvent,
    now: () => Date.now(),
    createObjectUrl: (blob) => URL.createObjectURL(blob),
    reload: () => window.location.reload(),
    freeBytes: async () => {
      try {
        const { quota, usage } = await navigator.storage.estimate()
        return quota === undefined || usage === undefined ? null : quota - usage
      } catch {
        return null
      }
    },
    bundledPath: MAP_SET_PATH,
    apiPrefix: API_PREFIX,
  })
  return session
}

/**
 * The URL of a map file in the bundle this page uses — for an `<img src>`
 * (a downloaded file is a `blob:` URL, which `img-src` allows). To read a
 * file use `fetchMapAsset`: the CSP's `connect-src` blocks `fetch(blob:…)`.
 */
export function resolveMapAsset(name: string): Promise<string> {
  return getMapBundleSession().assetUrl(name)
}

/** A map file of the bundle this page uses, as a `Response` — use it rather than `fetch(resolveMapAsset(…))`. */
export function fetchMapAsset(name: string): Promise<Response> {
  return getMapBundleSession().fetchAsset(name)
}
